import { Dialog, Disclosure, Menu, MenuButton, MenuItem, MenuItems, Popover, Transition } from '@headlessui/react'
import { ArrowUpRightIcon } from '@heroicons/react/20/solid'
import {
  Bars3Icon,
  ChatBubbleBottomCenterTextIcon,
  ChevronDownIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { ComponentProps, forwardRef, useEffect, useState } from 'react'
import { Link, NavLink, NavLinkProps, useLocation } from 'react-router-dom'

import { getOrganization } from '@/components/App/selectors'
import FeedbackDialog from '@/components/Feedback/Dialog'
import WorkgridIcon from '@/components/images/WorkgridIcon'
import { Role, useRoles } from '@/components/RolesContainer'
import useFeatures from '@/components/useFeatures'
import * as Feature from '@/constants/featureToggles'
import { pageRoutes } from '@/routes'
import { useAppSelector } from '@/store/hooks'
import { getCurrentSpace, getCurrentUser } from '@/store/selectors'
import cn from '@/utilities/cn'

import { useFeedbackButtonState } from './FeedbackButtonStateContext'
import SpaceSelect from './SpaceSelect'

type Flags = ReturnType<typeof useFeatures>
type NavItem = {
  label: string
  roles?: Role[]
  flags?: (flags: Flags) => boolean
  exact?: NavLinkProps['exact']
  isActive?: NavLinkProps['isActive']
} & ({ href: string; target?: string } | { children: NavItem[] })

const NAVBAR_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/',
    exact: true
  },
  {
    label: 'Apps',
    href: pageRoutes.SpaceAppBuilderPage.path,
    roles: pageRoutes.SpaceAppBuilderPage.requiresRoles,
    flags: flags => !flags[Feature.SPACE_APPS] && !flags[Feature.WORKSHOP_AGENTS],
    isActive: (_, l) => /^\/space\/.+\/appbuilder(?:\/(?!(?:templates|datasources)(?:\/|$)).*)?$/.test(l.pathname)
  },
  {
    label: 'Agents',
    href: pageRoutes.SpaceAppBuilderPage.path,
    roles: pageRoutes.SpaceAppBuilderPage.requiresRoles,
    flags: flags => !flags[Feature.SPACE_APPS] && !!flags[Feature.WORKSHOP_AGENTS],
    isActive: (_, l) => /^\/space\/.+\/appbuilder(?:\/(?!(?:templates|datasources)(?:\/|$)).*)?$/.test(l.pathname)
  },
  {
    label: 'Apps',
    roles: [...(pageRoutes.SpaceAppBuilderPage.requiresRoles || []), Role.SPACE_ADMIN_ROLE],
    flags: flags => !!flags[Feature.SPACE_APPS] && !flags[Feature.WORKSHOP_AGENTS],
    isActive: (_, l) => /^\/space\/.+\/(appbuilder($|\/[^()^ac-elmopr-u|])|apps)/.test(l.pathname),
    children: [
      {
        label: 'Builder',
        href: pageRoutes.SpaceAppBuilderPage.path,
        roles: pageRoutes.SpaceAppBuilderPage.requiresRoles,
        isActive: (_, l) => /^\/space\/.+\/appbuilder($|\/[^()^ac-elmopr-u|])/.test(l.pathname)
      },
      {
        label: 'Catalog',
        href: pageRoutes.SpaceAppsRedirect.path,
        roles: pageRoutes.SpaceAppsRedirect.requiresRoles
      }
    ]
  },
  {
    label: 'Agents',
    roles: [...(pageRoutes.SpaceAppBuilderPage.requiresRoles || []), Role.SPACE_ADMIN_ROLE],
    flags: flags => !!flags[Feature.SPACE_APPS] && !!flags[Feature.WORKSHOP_AGENTS],
    isActive: (_, l) => /^\/space\/.+\/(appbuilder($|\/[^()^ac-elmopr-u|])|apps)/.test(l.pathname),
    children: [
      {
        label: 'Builder',
        href: pageRoutes.SpaceAppBuilderPage.path,
        roles: pageRoutes.SpaceAppBuilderPage.requiresRoles,
        isActive: (_, l) => /^\/space\/.+\/appbuilder($|\/[^()^ac-elmopr-u|])/.test(l.pathname)
      },
      {
        label: 'Catalog',
        href: pageRoutes.SpaceAppsRedirect.path,
        roles: pageRoutes.SpaceAppsRedirect.requiresRoles
      }
    ]
  },
  {
    label: 'Templates',
    href: pageRoutes.SpaceAppBuilderCreateAppPage.path
  },
  {
    label: 'Data Sources',
    href: pageRoutes.SpaceAppBuilderDataSourcesPage.path,
    roles: pageRoutes.SpaceAppBuilderDataSourcesPage.requiresRoles
  },
  {
    label: 'Knowledge',
    roles: [...(pageRoutes.SpaceKnowledgeDocumentsPage.requiresRoles || []), Role.CHATBOT_ADMIN_ROLE],
    isActive: (_, l) => /^\/space\/.+\/knowledge(?:\/(?!(?:\/|$)).*)?$/.test(l.pathname),
    children: [
      {
        label: 'Documents',
        href: pageRoutes.SpaceKnowledgeDocumentsPage.path,
        roles: pageRoutes.SpaceKnowledgeDocumentsPage.requiresRoles
      },
      {
        label: 'Q&A',
        href: pageRoutes.SpaceKnowledgeQnASummaryPage.path,
        roles: pageRoutes.SpaceKnowledgeQnASummaryPage.requiresRoles
      },
      {
        label: 'Collections',
        href: pageRoutes.SpaceKnowledgeCollectionsPage.path,
        roles: pageRoutes.SpaceKnowledgeCollectionsPage.requiresRoles,
        flags: flags => !!flags[Feature.BEDROCK_KNOWLEDGE_SERVICE]
      }
    ]
  },
  {
    label: 'Publisher',
    href: pageRoutes.SpacePublisherPage.path,
    roles: pageRoutes.SpacePublisherPage.requiresRoles,
    flags: flags => !!flags[Feature.SPACE_PUBLISHER]
  },
  {
    label: 'Chatbot',
    href: pageRoutes.SpaceChatbotPage.path,
    roles: pageRoutes.SpaceChatbotPage.requiresRoles
  },
  {
    label: 'Playground',
    href: pageRoutes.SpacePlaygroundPage.path
  },
  {
    label: 'Notificator',
    href: pageRoutes.SpaceNotificatorPage.path,
    flags: flags => !!flags[Feature.SPACE_NOTIFICATOR]
  }
]

const NAVBAR_SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Organization management',
    items: [
      {
        label: 'My organization',
        href: pageRoutes.OrganizationSettingsPage.path,
        roles: pageRoutes.OrganizationSettingsPage.requiresRoles,
        exact: true
      },
      {
        label: 'Security',
        href: pageRoutes.OrganizationSecurityPage.path,
        roles: pageRoutes.OrganizationSecurityPage.requiresRoles
      },
      {
        label: 'Spaces',
        href: pageRoutes.OrganizationSpacesPage.path,
        roles: pageRoutes.OrganizationSpacesPage.requiresRoles
      },
      {
        label: 'Client configs',
        href: pageRoutes.OrganizationClientConfigsPage.path,
        roles: pageRoutes.OrganizationClientConfigsPage.requiresRoles
      },
      {
        label: 'Client credentials',
        href: pageRoutes.OrganizationToolbarsPage.path,
        roles: pageRoutes.OrganizationToolbarsPage.requiresRoles
      },
      {
        label: 'Channel Connections',
        href: pageRoutes.OrganizationChannelConnectionsPage.path,
        roles: pageRoutes.OrganizationChannelConnectionsPage.requiresRoles
      },
      {
        label: 'Audit logs',
        href: pageRoutes.OrganizationAuditPage.path,
        roles: pageRoutes.OrganizationAuditPage.requiresRoles
      }
    ]
  },
  {
    label: 'User management',
    items: [
      {
        label: 'Users',
        href: pageRoutes.OrganizationUsersPage.path,
        roles: pageRoutes.OrganizationUsersPage.requiresRoles,
        isActive: (_, l) => /^\/organization\/users($|\/[^()abegin-pr-v|])/.test(l.pathname)
      },
      {
        label: 'Groups',
        href: pageRoutes.OrganizationGroupsPage.path,
        roles: pageRoutes.OrganizationGroupsPage.requiresRoles
      },
      {
        label: 'User attributes',
        href: pageRoutes.OrganizationUserAttributesPage.path,
        roles: pageRoutes.OrganizationUserAttributesPage.requiresRoles
      },
      {
        label: 'User provisioning',
        href: pageRoutes.OrganizationUserProvisioningPage.path,
        roles: pageRoutes.OrganizationUserProvisioningPage.requiresRoles
      }
    ]
  },
  {
    label: 'Space management',
    items: [
      {
        label: 'Members',
        href: pageRoutes.SpaceMembersPage.path,
        roles: pageRoutes.SpaceMembersPage.requiresRoles,
        isActive: (_, l) => /^\/space\/.+\/members($|\/[^()elors])/.test(l.pathname)
      },
      {
        label: 'Roles',
        href: pageRoutes.SpaceMembersRolesPage.path,
        roles: pageRoutes.SpaceMembersRolesPage.requiresRoles
      },
      {
        label: 'Analytics',
        href: pageRoutes.SpaceAnalyticsPage.path,
        roles: pageRoutes.SpaceAnalyticsPage.requiresRoles
      },
      {
        label: 'Notification sources',
        href: pageRoutes.SpaceNotificationSourcesPage.path,
        roles: pageRoutes.SpaceNotificationSourcesPage.requiresRoles
      }
    ]
  },
  {
    label: 'Help',
    items: [
      {
        label: 'Help center',
        href: 'https://help.workgrid.com/s/',
        target: '_blank'
      },
      {
        label: 'Getting started',
        href: 'https://help.workgrid.com/s/getting-started-champion-guide/',
        target: '_blank'
      },
      {
        label: 'Tutorials',
        href: 'https://help.workgrid.com/s/tutorials',
        target: '_blank'
      },
      {
        label: 'Release notes',
        href: 'https://help.workgrid.com/s/article/Release-Notes',
        target: '_blank'
      },
      {
        label: 'Subscribe to updates',
        href: 'https://www.workgrid.com/subscribe-product-updates',
        target: '_blank'
      }
    ]
  }
]

function NavigationContainer() {
  const roles = useRoles()
  const features = useFeatures()

  const organization = useAppSelector(getOrganization)
  const currentSpace = useAppSelector(getCurrentSpace)
  const user = useAppSelector(getCurrentUser)

  const getNavItems = (items: NavItem[]): NavItem[] => {
    return items
      .map(item => {
        if ('href' in item) {
          const href = item.href.replace(':spaceId', currentSpace?.id || '')
          item = { ...item, href }
        }

        if ('children' in item) {
          const children = getNavItems(item.children)
          item = { ...item, children }
        }

        return item
      })
      .filter(item => {
        if ('children' in item && item.children.length === 0) return false

        if (item.roles && !roles.isAllowedAccess(item.roles, organization?.id, currentSpace?.id)) return false
        if (item.flags && !item.flags(features)) return false

        return true
      })
  }

  const items = getNavItems(NAVBAR_ITEMS)

  const sections = NAVBAR_SECTIONS.map(section => {
    const items = getNavItems(section.items)
    return { ...section, items }
  })
  const filtered = sections.filter(section => section.items.length)
  const offset = sections.length - filtered.length

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const [feedbackButtonState] = useFeedbackButtonState()
  const feedbackButtonIsActive = feedbackButtonState === 'active'

  const location = useLocation()
  useEffect(() => setMobileMenuOpen(false), [location])

  const loading = !organization || !currentSpace || !user

  return (
    <header className="relative isolate z-10 border-b border-zinc-100 bg-white">
      <nav className="mx-auto flex h-16 min-w-max items-center justify-center px-6 lg:px-8" aria-label="Global">
        <div className="flex flex-1 lg:flex-initial">
          <span className="sr-only">{organization?.name}</span>
          {organization?.logoUrl ? (
            <img className="h-8 w-8 rounded" src={organization.logoUrl} alt="" />
          ) : (
            <WorkgridIcon className="h-8 w-8 rounded" />
          )}
        </div>
        {loading ? (
          <div className="ml-6 flex flex-1 justify-between">
            <div className="hidden lg:flex lg:items-center lg:space-x-6">
              <div className="h-2 w-24 rounded bg-gray-200" />
              <div className="h-2 w-24 rounded bg-gray-200" />
              <div className="h-2 w-24 rounded bg-gray-200" />
            </div>
            <div className="flex flex-1 items-center justify-end space-x-6">
              <div className="h-2 w-24 rounded bg-gray-200" />
            </div>
          </div>
        ) : (
          <>
            <div className="hidden lg:ml-6 lg:flex lg:h-full lg:flex-1 lg:gap-x-6">
              {items.map(item => {
                if ('href' in item) {
                  return (
                    <UniversalNavLink
                      key={item.href}
                      to={item.href}
                      className="-mb-px inline-flex items-center border-b-2 border-transparent px-1 pt-1 text-sm font-medium text-zinc-500 hover:border-zinc-400 hover:text-gray-900"
                      activeClassName="border-blue-700 text-gray-900"
                      exact={item.exact}
                      isActive={item.isActive}
                      target={item.target}
                    >
                      {item.label}
                    </UniversalNavLink>
                  )
                }

                if ('children' in item) {
                  return (
                    <Menu key={item.label} as="div" className="relative inline-flex">
                      <MenuButton
                        className={cn(
                          'group -mb-px inline-flex items-center border-b-2 border-transparent px-1 pt-1 text-sm font-medium text-zinc-500 hover:border-zinc-400 hover:text-gray-900',
                          item.isActive?.(null, location) && 'border-blue-700 text-gray-900'
                        )}
                      >
                        <span>{item.label}</span>
                        <ChevronDownIcon
                          className="ml-1 h-3 w-3 text-zinc-500 group-hover:text-gray-900"
                          aria-hidden="true"
                        />
                      </MenuButton>

                      <Transition
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <MenuItems className="absolute top-full left-0 z-10 -mt-4 w-48 min-w-min origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                          {item.children.map(item => {
                            if ('children' in item) return null

                            return (
                              <MenuItem key={item.href}>
                                {({ focus }) => (
                                  <Link
                                    key={item.href}
                                    to={item.href}
                                    className={cn(
                                      focus ? 'bg-gray-100' : '',
                                      'inline-flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-sm text-gray-700'
                                    )}
                                  >
                                    {item.label}
                                  </Link>
                                )}
                              </MenuItem>
                            )
                          })}
                        </MenuItems>
                      </Transition>
                    </Menu>
                  )
                }

                return null
              })}
            </div>
            <div className="flex items-center justify-end gap-x-6">
              <SpaceSelect />

              <FeedbackDialog>
                {({ showDialog }) => (
                  <button
                    type="button"
                    className={cn('flex overflow-hidden rounded bg-white text-gray-400 hover:text-gray-500', {
                      'text-blue-600 hover:text-blue-600': feedbackButtonIsActive
                    })}
                    onClick={showDialog}
                    disabled={feedbackButtonIsActive}
                  >
                    <span className="sr-only">Submit feedback</span>
                    <ChatBubbleBottomCenterTextIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                )}
              </FeedbackDialog>

              <div className="flex lg:hidden">
                <button
                  type="button"
                  className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-400 hover:text-gray-500"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <Popover className="hidden lg:flex">
                <Popover.Button className="flex items-center gap-x-1 text-sm leading-6 text-gray-400 hover:text-gray-500">
                  {({ open }) =>
                    open ? (
                      <>
                        <span className="sr-only">Close main menu</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </>
                    ) : (
                      <>
                        <span className="sr-only">Open main menu</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                      </>
                    )
                  }
                </Popover.Button>

                <Transition
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 -translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-1"
                >
                  <Popover.Panel className="absolute inset-x-0 top-0 -z-10 bg-white pt-14 shadow-lg ring-1 ring-gray-900/5">
                    {({ close }) => (
                      <div className="mx-auto min-w-full max-w-max px-6 py-10 lg:px-8">
                        <div className="flex gap-8">
                          <div className="grid flex-1 grid-cols-3 gap-16">
                            <div>
                              <h1 className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-black">
                                {organization?.name}
                              </h1>

                              {organization?.supportUrl ? (
                                <div className="mt-8">
                                  <h2 className="text-xs font-semibold leading-tight text-zinc-900">Support URL</h2>
                                  <a
                                    href={
                                      (organization.supportUrl.includes('@') ? 'mailto:' : '') + organization.supportUrl
                                    }
                                    className="text-xs font-normal text-blue-700 hover:underline"
                                  >
                                    {organization.supportUrl}
                                  </a>
                                </div>
                              ) : null}
                            </div>

                            <div
                              className="col-span-2 grid gap-8"
                              style={{ gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` }}
                            >
                              {offset ? <div style={{ gridColumn: `span ${offset} / span ${offset}` }} /> : null}

                              {filtered.map(section => {
                                return (
                                  <div key={section.label}>
                                    <h2 className="text-xs font-semibold leading-tight text-gray-900">
                                      {section.label}
                                    </h2>
                                    <ul className="mt-1">
                                      {section.items.map(item => {
                                        if ('children' in item) return null

                                        return (
                                          <li key={item.href}>
                                            <UniversalNavLink
                                              to={item.href}
                                              className="inline-flex items-center justify-center gap-0.5 text-xs text-zinc-500 hover:text-blue-700 hover:underline"
                                              activeClassName="font-bold text-gray-900"
                                              exact={item.exact}
                                              isActive={item.isActive}
                                              target={item.target}
                                              onClick={() => close()}
                                            >
                                              {item.label}
                                            </UniversalNavLink>
                                          </li>
                                        )
                                      })}
                                    </ul>
                                  </div>
                                )
                              })}
                            </div>
                          </div>

                          <div className="w-px bg-zinc-300" />

                          <div className="w-48 xl:w-64">
                            <h2 className="text-xs font-semibold leading-tight text-gray-900">Profile</h2>

                            <div className="mt-1">
                              {user?.photos?.[0]?.value ? (
                                <img className="h-12 w-12 rounded-full" src={user.photos[0].value} alt="" />
                              ) : (
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-300">
                                  <UserIcon className="h-2/3 w-2/3" />
                                </div>
                              )}
                            </div>

                            <div className="mt-3">
                              <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold text-zinc-900">
                                {user?.displayName}
                              </h3>
                              <h4 className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-normal text-zinc-900">
                                {user?.email}
                              </h4>
                            </div>

                            <div className="mt-3">
                              <Link to="/logout" className="text-xs font-normal text-blue-700 hover:underline">
                                Sign out
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Popover.Panel>
                </Transition>
              </Popover>
            </div>
          </>
        )}
      </nav>

      <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6">
          <div className="-mt-6 flex h-16 items-center justify-between">
            <span className="sr-only">{organization?.name}</span>
            {organization?.logoUrl ? (
              <img className="h-8 w-8 rounded" src={organization.logoUrl} alt="" />
            ) : (
              <WorkgridIcon className="h-8 w-8 rounded" />
            )}

            <div className="flex items-center justify-end gap-x-6">
              <SpaceSelect />

              <FeedbackDialog>
                {({ showDialog }) => (
                  <button
                    type="button"
                    className={cn('flex overflow-hidden rounded bg-white text-gray-400 hover:text-gray-500', {
                      'text-blue-600 hover:text-blue-600': feedbackButtonIsActive
                    })}
                    onClick={showDialog}
                    disabled={feedbackButtonIsActive}
                  >
                    <span className="sr-only">Submit feedback</span>
                    <ChatBubbleBottomCenterTextIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                )}
              </FeedbackDialog>

              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-400 hover:text-gray-500"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="-my-6 divide-y divide-gray-500/10">
            <div className="py-6">
              {items.map(item => {
                if ('href' in item) {
                  return (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      className="-mx-3 flex items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
                      activeClassName="font-bold text-gray-900"
                      exact={item.exact}
                      isActive={item.isActive}
                    >
                      {item.label}
                    </NavLink>
                  )
                }

                if ('children' in item) {
                  return (
                    <Disclosure key={item.label} as="div" className="-mx-3">
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-sm font-medium text-gray-900 hover:bg-gray-50">
                            {item.label}
                            <ChevronDownIcon
                              className={cn(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                              aria-hidden="true"
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel>
                            {item.children.map(item => {
                              if ('children' in item) return null

                              return (
                                <UniversalNavLink
                                  key={item.href}
                                  to={item.href}
                                  className="flex items-center rounded-lg py-2 pl-6 pr-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
                                  activeClassName="font-bold text-gray-900"
                                  exact={item.exact}
                                  isActive={item.isActive}
                                  target={item.target}
                                >
                                  {item.label}
                                </UniversalNavLink>
                              )
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  )
                }

                return null
              })}
            </div>
            <div className="py-6">
              <div className="mb-4">
                <h2 className="overflow-hidden text-ellipsis whitespace-nowrap text-xl font-semibold text-black">
                  {organization?.name}
                </h2>

                {organization?.supportUrl ? (
                  <div className="mt-3">
                    <h2 className="text-xs font-semibold leading-tight text-zinc-900">Support URL</h2>
                    <a
                      href={(organization.supportUrl.includes('@') ? 'mailto:' : '') + organization.supportUrl}
                      className="text-xs font-normal text-blue-700 hover:underline"
                    >
                      {organization.supportUrl}
                    </a>
                  </div>
                ) : null}
              </div>

              {filtered.map(section => {
                return (
                  <Disclosure key={section.label} as="div" className="-mx-3">
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-sm font-medium text-gray-900 hover:bg-gray-50">
                          {section.label}
                          <ChevronDownIcon
                            className={cn(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                            aria-hidden="true"
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          {section.items.map(item => {
                            if ('children' in item) return null

                            return (
                              <UniversalNavLink
                                key={item.href}
                                to={item.href}
                                className="flex items-center rounded-lg py-2 pl-6 pr-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
                                activeClassName="font-bold text-gray-900"
                                exact={item.exact}
                                isActive={item.isActive}
                                target={item.target}
                              >
                                {item.label}
                              </UniversalNavLink>
                            )
                          })}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                )
              })}
            </div>

            <div className="py-6">
              {user?.photos?.[0]?.value ? (
                <img className="h-16 w-16 rounded-full" src={user.photos[0].value} alt="" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-300">
                  <UserIcon className="h-2/3 w-2/3" />
                </div>
              )}

              <div className="mt-3">
                <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold text-zinc-900">
                  {user?.displayName}
                </h3>
                <h4 className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-normal text-zinc-900">
                  {user?.email}
                </h4>
              </div>

              <div className="mt-3">
                <Link to="/logout" className="text-xs font-normal text-blue-700 hover:underline">
                  Sign out
                </Link>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  )
}

export default NavigationContainer

const UniversalNavLink = forwardRef<HTMLAnchorElement, ComponentProps<NavLink>>((props, ref) => {
  if (typeof props.to === 'string' && props.to.startsWith('http')) {
    const { to, style, className, children, activeStyle, activeClassName, isActive, ...rest } = props
    return (
      <a
        ref={ref}
        href={to}
        style={typeof style === 'function' ? style(false) : style}
        className={typeof className === 'function' ? className(false) : className}
        {...rest}
      >
        {children}
        <ArrowUpRightIcon className="h-4 w-4 flex-shrink-0" />
      </a>
    )
  }

  return <NavLink ref={ref} {...props} />
})

UniversalNavLink.displayName = 'UniversalNavLink'
